import { CommonModule } from '@angular/common';
import { Component, ContentChild, EventEmitter, Input, Output, TemplateRef, ViewChild, booleanAttribute } from '@angular/core';
import { LazyLoadEvent } from 'primeng/api';
import { PaginatorModule } from 'primeng/paginator';
import { Table, TableModule } from 'primeng/table';
import { UiTableBodyDirective } from './ui-table-body.directive';
import { UiTableBodyCellsDirective } from './ui-table-body-cells.directive';
import { UiTableHeaderDirective } from './ui-table-header.directive';
import { UiNoData } from '../ui-no-data/ui-no-data.component';
import { DebounceInputDirective } from '../../directives/debounce-input.directive';

export interface UiTableColumn {
  field?: string;
  header: string;
  sortable?: boolean;
  class?: string;
}

@Component({
  selector: 'ui-table',
  templateUrl: './ui-table.component.html',
  styleUrl: './ui-table.component.scss',
  standalone: true,
  imports: [CommonModule, TableModule, PaginatorModule, UiNoData, UiTableBodyCellsDirective, DebounceInputDirective]
})
export class UiTableComponent {
  @ViewChild('tt') tableRef!: Table;

  @Input() data: any[] = [];
  @Input() first: number = 0;
  @Input() rows: number = 10;
  @Input() totalRecords: number = 0;
  @Input() lazy: boolean = false;
  @Input() rowHover: boolean = true;
  @Input() sortField: string = '';
  @Input() sortOrder: number = 0;
  @Input() showPaginator: boolean = true;
  @Input() dataKey: string = 'Id';
  @Input() tableClass: string = '';
  @Input() selectionMode: string = '';
  @Input() selection: any[] = [];
  @Output() selectionChange = new EventEmitter<any[]>();
  @Input({ transform: booleanAttribute }) showCheckboxes: boolean = false;

  /** Колонки для авто-генерации заголовка (если не передан appUiTableHeader). */
  @Input() columns: UiTableColumn[] = [];

  /** Шаблон заголовка через директиву <ng-template appUiTableHeader>. */
  @ContentChild(UiTableHeaderDirective) headerDirective?: UiTableHeaderDirective;

  /** Шаблон тела строки через директиву <ng-template appUiTableBody>. */
  @ContentChild(UiTableBodyDirective) bodyDirective?: UiTableBodyDirective;

  /** Шаблон ячеек строки (только <td>) для режима с чекбоксами. */
  @ContentChild(UiTableBodyCellsDirective) bodyCellsDirective?: UiTableBodyCellsDirective;

  /** Fallback: шаблон тела как @Input (обратная совместимость). */
  @Input() bodyTemplate: TemplateRef<any> | null = null;

  /** Итоговый шаблон строки: приоритет у проекции. */
  get rowTemplate(): TemplateRef<any> | null {
    return this.bodyDirective?.template ?? this.bodyTemplate;
  }

  @Output() onLazyLoad = new EventEmitter<LazyLoadEvent>();
  @Output() onSort = new EventEmitter<{ field: string; order: number }>();

  getTableCountText(): string {
    const start = Math.min(this.first + 1, this.totalRecords);
    const end = Math.min(this.first + this.rows, this.totalRecords);
    return `${start} - ${end}`;
  }

  handleLazyLoad(event: LazyLoadEvent) {
    this.onLazyLoad.emit(event);
  }

  handleSort(event: { field: string; order: number }) {
    this.onSort.emit(event);
  }
}
